import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useTheme } from "@/App";
import { asArray } from "@/lib/arrays";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, Video, Users, TrendingUp, Bell, Clock,
  ChevronRight, Copy, CheckCircle, Zap, Trophy, Target, Award,
  ArrowUpRight, ArrowDownRight, BookMarked, Star, X,
  Calculator, Newspaper, Lock, Sparkles, Play, Radio, Calendar,
  Check, ShieldCheck, Flame, GraduationCap,
  Megaphone, CalendarClock, ShieldAlert, CheckCheck, MessageSquare, BellRing, ArrowRight,
  Gift, Tag, Layers, AlertCircle, Loader2, Plus
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { startRazorpayCourseCheckout } from "@/lib/payment";

// Strictly exclude market briefs from the dashboard broadcast feed
const isBroadcastOrAlert = (item) => {
  if (!item) return false;
  const type = (item.type || "").toLowerCase();
  const title = (item.title || "").toLowerCase();
  const message = (item.message || "").toLowerCase();

  // Exclude market briefs completely (they go only to the top header bell notification)
  if (type === "market_brief" || type === "brief" || type === "market_update") return false;
  if (title.includes("market brief") || title.includes("brief is live") || title.includes("post-market") || title.includes("pre-market")) return false;
  if (message.includes("nifty settle at") || message.includes("market slides post closing") || message.includes("pre-market outlook")) return false;

  return true;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
};

const StudentDashboard = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const isLight = theme === "light";
  const hasDrishtiAccess = Boolean(
    user?.role === "admin" || 
    user?.role === "superadmin" || 
    user?.has_drishti_access === true
  );

  // Course & Session State
  const [courses, setCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [progress, setProgress] = useState({});
  const [referralStats, setReferralStats] = useState(null);
  const [tradeOfWeek, setTradeOfWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Course Filter Tab: "all" | "live" | "recorded"
  const [courseFilter, setCourseFilter] = useState(() => {
    if (location.hash === "#recorded-courses") return "recorded";
    if (location.hash === "#live-courses") return "live";
    return "all";
  });

  useEffect(() => {
    if (location.hash === "#recorded-courses") {
      setCourseFilter("recorded");
    } else if (location.hash === "#live-courses") {
      setCourseFilter("live");
    }
  }, [location.hash]);

  // Enrollment Modal States
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollType, setEnrollType] = useState("trial");
  const [enrolling, setEnrolling] = useState(false);
  const [payingOnline, setPayingOnline] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dashboardRefCode, setDashboardRefCode] = useState(() => {
    if (typeof window === "undefined") return "";
    return (sessionStorage.getItem("jamadhan_referral_code") || localStorage.getItem("jamadhan_referral_code") || "").trim().toUpperCase();
  });
  const [showDashboardRefInput, setShowDashboardRefInput] = useState(Boolean(dashboardRefCode));

  // Broadcasts & Live Notification System
  const [broadcasts, setBroadcasts] = useState([]);
  const [unreadBroadcastsCount, setUnreadBroadcastsCount] = useState(0);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [broadcastMobileTab, setBroadcastMobileTab] = useState("message"); // "message" | "list"
  const [dashboardBatches, setDashboardBatches] = useState([]);
  const [enrollingBatchId, setEnrollingBatchId] = useState(null);

  const handleEnrollBatch = async (batch) => {
    setEnrollingBatchId(batch.batch_id);
    try {
      await axios.post(`${API}/batches/${batch.batch_id}/enroll`, {}, { withCredentials: true });
      toast.success("Enrollment request sent! Admin will approve shortly.");
      setDashboardBatches(prev => prev.map(b => b.batch_id === batch.batch_id ? { ...b, my_enrollment_status: "pending" } : b));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send enrollment request");
    } finally {
      setEnrollingBatchId(null);
    }
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const [coursesRes, liveClassesRes, referralRes, notifRes, batchesRes] = await Promise.allSettled([
        axios.get(`${API}/courses`, { withCredentials: true }),
        axios.get(`${API}/live-classes`, { withCredentials: true }),
        axios.get(`${API}/referrals/my-stats`, { withCredentials: true }),
        axios.get(`${API}/notifications`, { withCredentials: true }),
        axios.get(`${API}/batches`, { withCredentials: true })
      ]);
      const unwrap = (res, fallback) => res.status === "fulfilled" ? res.value.data : fallback;
      const allCourseItems = asArray(unwrap(coursesRes, []), "courses");
      const allBatches = asArray(unwrap(batchesRes, []));
      const enrolledCoursesIds = user?.enrolled_courses || [];
      const isStaffUser = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'mentor';

      setDashboardBatches(allBatches);

      // Helper to identify static placeholder course created for Trading Ki Pathshala
      const isPathshalaCourse = (c) => {
        const t = (c.title || "").toLowerCase();
        const id = (c.course_id || "").toLowerCase();
        return t.includes("trading ki pathshala") || t.includes("trading ki pathsala") || id.includes("pathshala") || id.includes("pathsala");
      };

      let enrolledItems = [];
      let availableItems = [];

      if (isStaffUser) {
        // Staff has access to everything; filter out any static placeholder course
        enrolledItems = allCourseItems.filter(c => !isPathshalaCourse(c));
        availableItems = [];
      } else {
        const enrolled = allCourseItems.filter(c => enrolledCoursesIds.includes(c.course_id));
        enrolledItems = enrolled.filter(c => !isPathshalaCourse(c));
        const enrolledIdSet = new Set(enrolledItems.map(c => c.course_id));
        availableItems = allCourseItems.filter(c => !enrolledIdSet.has(c.course_id) && !isPathshalaCourse(c));
      }

      setCourses(enrolledItems);
      setAvailableCourses(availableItems);
      setLiveClasses(asArray(unwrap(liveClassesRes, []), "classes"));
      setReferralStats(unwrap(referralRes, {}));

      // Parse notifications/broadcasts (strictly filtering out market briefs)
      if (notifRes.status === "fulfilled" && notifRes.value?.data) {
        const notifs = asArray(notifRes.value.data, "notifications");
        const filtered = notifs.filter(isBroadcastOrAlert);
        const sorted = filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        setBroadcasts(sorted);
        const unread = sorted.filter(n => !n.read_by?.includes(user?.user_id)).length;
        setUnreadBroadcastsCount(unread);
      }

      // Fetch trade of the week (non-blocking)
      try {
        const totwRes = await axios.get(`${API}/journal/trade-of-the-week`, { withCredentials: true });
        if (totwRes.data) setTradeOfWeek(totwRes.data);
      } catch (e) {
        // No trade of week yet - silent
      }

      // Track progress for enrolled items (limit to first 6 to avoid network congestion)
      const coursesToTrack = enrolledItems.slice(0, 6);
      if (coursesToTrack.length > 0) {
        const progressResults = await Promise.allSettled(
          coursesToTrack.map(course =>
            axios.get(`${API}/progress/${course.course_id}`, { withCredentials: true })
          )
        );
        const progressMap = {};
        progressResults.forEach((res, idx) => {
          if (res.status === "fulfilled" && res.value?.data) {
            progressMap[coursesToTrack[idx].course_id] = res.value.data;
          }
        });
        setProgress(progressMap);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error); // eslint-disable-line no-console
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDashboardData();
    // 30s live poll for fresh broadcast notifications (excluding market briefs)
    const iv = setInterval(async () => {
      try {
        const notifRes = await axios.get(`${API}/notifications`, { withCredentials: true });
        if (notifRes.data) {
          const notifs = asArray(notifRes.data, "notifications");
          const filtered = notifs.filter(isBroadcastOrAlert);
          const sorted = filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          setBroadcasts(sorted);
          const unread = sorted.filter(n => !n.read_by?.includes(user?.user_id)).length;
          setUnreadBroadcastsCount(unread);
        }
      } catch (e) {
        // silent polling
      }
    }, 30000);
    return () => clearInterval(iv);
  }, [fetchDashboardData, user?.user_id]);

  const latestBroadcast = useMemo(() => {
    return broadcasts.length > 0 ? broadcasts[0] : null;
  }, [broadcasts]);

  const handleOpenBroadcastModal = (broadcast = null) => {
    const target = broadcast || latestBroadcast || broadcasts[0] || null;
    setSelectedBroadcast(target);
    setBroadcastMobileTab("message");
    setIsBroadcastModalOpen(true);
  };

  const handleMarkAsRead = async () => {
    try {
      setMarkingRead(true);
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      setBroadcasts(prev => prev.map(b => ({
        ...b,
        read_by: [...(b.read_by || []), user?.user_id]
      })));
      setUnreadBroadcastsCount(0);
      toast.success("All announcements marked as read");
    } catch (e) {
      toast.error("Failed to mark announcements read");
    } finally {
      setMarkingRead(false);
    }
  };

  const getBroadcastMeta = (type, title = "") => {
    const t = (type || "").toLowerCase();
    const lowerTitle = (title || "").toLowerCase();

    if (t === "emergency") {
      return {
        label: "Emergency Alert",
        icon: ShieldAlert,
        color: "text-rose-500",
        bg: "bg-rose-500/10",
        border: "border-rose-500/30",
        badge: "bg-rose-500 text-white font-bold"
      };
    }
    if (t === "class_reminder" || lowerTitle.includes("class") || lowerTitle.includes("batch")) {
      return {
        label: "Live Class Reminder",
        icon: CalendarClock,
        color: "text-sky-500",
        bg: "bg-sky-500/10",
        border: "border-sky-500/30",
        badge: "bg-sky-500 text-white font-bold"
      };
    }
    if (t === "alert" || t === "drishti_alert" || lowerTitle.includes("drishti ai") || lowerTitle.includes("demand") || lowerTitle.includes("supply")) {
      return {
        label: "Drishti AI Trade Alert",
        icon: Zap,
        color: "text-amber-500",
        bg: "bg-amber-500/10",
        border: "border-amber-500/30",
        badge: "bg-amber-500 text-slate-950 font-bold"
      };
    }
    if (t === "announcement" || lowerTitle.includes("announcement")) {
      return {
        label: "Official Announcement",
        icon: Sparkles,
        color: "text-emerald-500",
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/30",
        badge: "bg-emerald-500 text-slate-950 font-bold"
      };
    }
    return {
      label: "Admin Broadcast",
      icon: Megaphone,
      color: "text-indigo-500",
      bg: "bg-indigo-500/10",
      border: "border-indigo-500/30",
      badge: "bg-indigo-600 text-white font-bold"
    };
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const submitEnrollmentRequest = async () => {
    if (!selectedCourse) return;
    setEnrolling(true);
    try {
      await axios.post(`${API}/enrollments/request`, {
        course_id: selectedCourse.course_id,
        request_type: enrollType
      }, { withCredentials: true });
      toast.success("Enrollment request submitted! Access to both Live & Recorded courses will be granted upon admin review.");
      setShowEnrollModal(false);
      setSelectedCourse(null);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.detail) {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Failed to send request. Please try again.");
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleOnlineCoursePayment = async () => {
    if (!selectedCourse) return;
    setPayingOnline(true);
    await startRazorpayCourseCheckout({
      courseId: selectedCourse.course_id,
      user,
      referralCode: dashboardRefCode.trim().toUpperCase() || undefined,
      onSuccess: () => {
        setPayingOnline(false);
        setShowEnrollModal(false);
        setSelectedCourse(null);
        fetchDashboardData();
      },
      onError: () => {
        setPayingOnline(false);
      }
    });
  };

  const overallProgress = useMemo(() => {
    return courses.length > 0
      ? Math.round(courses.reduce((sum, c) => sum + (progress[c.course_id]?.progress_percentage || 0), 0) / courses.length)
      : 0;
  }, [courses, progress]);

  // Find next upcoming live class
  const upcomingLiveClass = useMemo(() => {
    const now = new Date().toISOString();
    return liveClasses
      .filter(c => c.status === "live" || (c.status === "upcoming" && (!c.scheduled_at || c.scheduled_at >= now)))
      .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""))[0] || null;
  }, [liveClasses]);

  const continuePath = courses[0]?.course_id ? `/course/${courses[0].course_id}` : "/";

  if (loading) {
    return (
      <DashboardLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-6" data-testid="student-dashboard">
        
        {/* ═══ Apple-Grade Welcome Executive Banner ═══ */}
        <div className={`relative overflow-hidden rounded-2xl p-6 md:p-8 transition-all duration-300 border ${
          isLight 
            ? "bg-gradient-to-br from-white via-slate-50 to-emerald-50/40 border-slate-200/80 shadow-sm" 
            : "bg-gradient-to-br from-[#0F172A] via-[#0B0F19] to-[#050811] border-white/[0.08] shadow-2xl"
        }`} data-testid="welcome-banner">
          {/* Subtle Ambient Glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#00FF88]/15 via-[#00F0FF]/10 to-transparent rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 left-1/3 w-80 h-80 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-jetbrains font-semibold tracking-wider uppercase border border-emerald-500/20 bg-emerald-500/10 text-emerald-600 dark:text-[#00FF88]">
                <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                <span>Unified Learning Terminal</span>
                <span className="opacity-40">•</span>
                <span>Live &amp; Recorded Dual Access</span>
              </div>
              
              <h1 className="text-2xl md:text-3xl font-extrabold font-outfit text-gray-900 dark:text-white tracking-tight">
                Welcome back, {user?.name?.split(" ")[0]}
              </h1>
              
              <p className={`text-sm font-plex leading-relaxed ${isLight ? "text-slate-600" : "text-slate-300"}`}>
                Your institutional dashboard for live trading batches and self-paced masterclasses. Access interactive batch sessions and recorded video archives seamlessly.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Link to="/live-classes">
                <Button 
                  variant="outline" 
                  className={`font-semibold text-xs border rounded-xl px-4 py-2.5 h-auto transition-all ${
                    isLight 
                      ? "border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-100 text-slate-900 shadow-xs" 
                      : "border-white/10 hover:border-emerald-500/40 bg-white/[0.04] text-white"
                  }`}
                >
                  <Video className="w-4 h-4 mr-2 text-rose-500" />
                  Live Batches
                </Button>
              </Link>
              
              <Link to={continuePath}>
                <Button
                  disabled={!courses.length}
                  className="bg-gradient-to-r from-[#00FF88] to-[#00E5FF] hover:brightness-110 text-black font-bold text-xs rounded-xl px-5 py-2.5 h-auto shadow-[0_4px_20px_rgba(0,255,136,0.25)] transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="continue-learning-btn"
                >
                  <span>{courses.length ? "Continue Learning" : "Explore Courses"}</span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* ═══ 4 Core Executive Metrics ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-grid">
          {[
            { 
              label: "Enrolled Courses", 
              value: courses.length, 
              badge: "Dual Access",
              subtext: "Live Batches + Recorded Library",
              icon: GraduationCap, 
              color: isLight ? "#0284C7" : "#00F0FF",
              bg: isLight ? "bg-sky-500/10" : "bg-[#00F0FF]/10"
            },
            { 
              label: "Curriculum Progress", 
              value: `${overallProgress}%`, 
              badge: overallProgress >= 80 ? "Mastery" : overallProgress >= 40 ? "Active" : "Beginner",
              subtext: "Aggregate completion rate",
              icon: Target, 
              color: isLight ? "#059669" : "#00FF88",
              bg: isLight ? "bg-emerald-500/10" : "bg-[#00FF88]/10"
            },
            { 
              label: "Referral Network", 
              value: referralStats?.successful_referrals || 0, 
              badge: "Active",
              subtext: "Community rewards earned",
              icon: Users, 
              color: isLight ? "#D97706" : "#FFCC00",
              bg: isLight ? "bg-amber-500/10" : "bg-[#FFCC00]/10"
            },
            { 
              id: "broadcasts",
              label: "Admin Broadcasts", 
              value: unreadBroadcastsCount > 0 ? "New Message" : "Notice Board", 
              badge: unreadBroadcastsCount > 0 ? "Live Notice" : "Official Feed",
              subtext: latestBroadcast ? latestBroadcast.title : "All messages read",
              icon: Megaphone, 
              color: isLight ? "#4F46E5" : "#818CF8",
              bg: isLight ? "bg-indigo-500/10" : "bg-indigo-500/10",
              isClickable: true,
              onClick: () => handleOpenBroadcastModal(latestBroadcast || broadcasts[0])
            }
          ].map((stat, i) => {
            const isBroadcastCard = stat.id === "broadcasts";
            const hasNewAlert = isBroadcastCard && unreadBroadcastsCount > 0;

            const cardContent = (
              <>
                {/* Glowing neon accent line at top (ONLY when new message arrives) */}
                {hasNewAlert && (
                  <div className="absolute top-0 left-0 right-0 h-[2.5px] bg-gradient-to-r from-indigo-500 via-emerald-400 to-purple-500 animate-pulse" />
                )}

                <div className="flex items-center justify-between mb-3">
                  {/* Left Icon */}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${
                    hasNewAlert
                      ? "bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30"
                      : stat.bg
                  }`}>
                    <stat.icon
                      className={`w-5 h-5 ${hasNewAlert ? "animate-bounce [animation-duration:2.5s]" : ""}`}
                      style={{ color: hasNewAlert ? "#FFFFFF" : stat.color }}
                      strokeWidth={1.75}
                    />
                  </div>

                  {/* Center Radar Ping Beacon (ONLY when new message arrives, as circled by user in Image 2) */}
                  {hasNewAlert && (
                    <div className="relative flex items-center justify-center" title={`${unreadBroadcastsCount} new broadcast alert${unreadBroadcastsCount > 1 ? "s" : ""}`}>
                      <span className="animate-ping absolute inline-flex h-5 w-5 rounded-full bg-emerald-400 opacity-75" />
                      <span className="animate-pulse absolute inline-flex h-3.5 w-3.5 rounded-full bg-emerald-500/50" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400 shadow-[0_0_12px_#10B981]" />
                    </div>
                  )}

                  {/* Top Right Badge */}
                  <span className={`text-[10px] font-jetbrains font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 transition-all ${
                    hasNewAlert
                      ? "bg-rose-500/15 text-rose-500 dark:text-rose-400 border-rose-500/30 animate-pulse font-bold"
                      : isLight 
                        ? "bg-slate-100 text-slate-700 border-slate-200" 
                        : "bg-white/[0.04] text-slate-300 border-white/[0.08]"
                  }`}>
                    {hasNewAlert ? (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping inline-block" />
                        <span>NEW</span>
                      </>
                    ) : (
                      stat.badge
                    )}
                  </span>
                </div>

                {/* Metric Value */}
                <p className={`text-2xl sm:text-3xl font-extrabold font-jetbrains tracking-tight transition-all ${
                  hasNewAlert
                    ? "bg-gradient-to-r from-indigo-600 via-purple-600 to-emerald-600 dark:from-indigo-400 dark:via-purple-300 dark:to-emerald-400 bg-clip-text text-transparent"
                    : "text-gray-900 dark:text-white"
                }`}>
                  {stat.value}
                </p>

                {/* Bottom Label + Subtext + Arrow */}
                <div className="mt-1 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-semibold font-outfit ${
                      hasNewAlert ? "text-indigo-600 dark:text-indigo-300 font-bold" : "text-gray-800 dark:text-slate-200"
                    }`}>
                      {stat.label}
                    </p>
                    <p className={`text-[11px] font-plex truncate ${
                      hasNewAlert ? "text-indigo-500/80 dark:text-indigo-300/80 font-medium" : "text-gray-400 dark:text-slate-400"
                    }`}>
                      {stat.subtext}
                    </p>
                  </div>
                  {stat.isClickable && (
                    <ChevronRight className={`w-4 h-4 shrink-0 ml-1 transition-all ${
                      hasNewAlert
                        ? "text-indigo-500 animate-pulse translate-x-0.5"
                        : "text-slate-400 group-hover:text-indigo-500 group-hover:translate-x-1"
                    }`} />
                  )}
                </div>
              </>
            );

            if (hasNewAlert) {
              return (
                <motion.div
                  key={i}
                  onClick={stat.onClick}
                  data-testid={`stat-card-${stat.id || i}`}
                  initial={{ scale: 0.96 }}
                  animate={{
                    scale: [1, 1.025, 0.995, 1.02, 1],
                    boxShadow: isLight
                      ? [
                          "0 4px 15px rgba(99,102,241,0.12)",
                          "0 8px 30px rgba(99,102,241,0.25)",
                          "0 4px 15px rgba(99,102,241,0.12)"
                        ]
                      : [
                          "0 4px 20px rgba(99,102,241,0.15)",
                          "0 8px 35px rgba(99,102,241,0.35)",
                          "0 4px 20px rgba(99,102,241,0.15)"
                        ]
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 3,
                    ease: "easeInOut"
                  }}
                  className={`rounded-2xl p-4 sm:p-5 border transition-all duration-300 relative overflow-hidden group cursor-pointer active:scale-[0.98] ${
                    isLight
                      ? "bg-gradient-to-br from-white via-indigo-50/50 to-white border-indigo-400 ring-2 ring-indigo-400/30"
                      : "bg-gradient-to-br from-[#0F172A] via-indigo-950/40 to-[#0F172A] border-indigo-500/70 ring-2 ring-indigo-500/40"
                  }`}
                >
                  {cardContent}
                </motion.div>
              );
            }

            return (
              <div 
                key={i} 
                onClick={stat.onClick}
                data-testid={`stat-card-${stat.id || i}`}
                className={`rounded-2xl p-4 sm:p-5 border transition-all duration-300 relative overflow-hidden group ${
                  stat.isClickable ? "cursor-pointer active:scale-[0.98] hover:border-indigo-500/50 hover:shadow-lg" : ""
                } ${
                  isLight 
                    ? "bg-white border-slate-200/80 hover:shadow-md hover:border-slate-300" 
                    : "bg-[#0F172A]/60 backdrop-blur-xl border-white/[0.06] hover:border-white/20 shadow-lg"
                }`}
              >
                {cardContent}
              </div>
            );
          })}
        </div>

        {/* ═══ Overall Progress Completion Bar ═══ */}
        <div className={`rounded-2xl p-5 border ${
          isLight ? "bg-white border-slate-200/80 shadow-sm" : "bg-[#0F172A]/50 border-white/[0.06] shadow-xl"
        }`} data-testid="overall-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                <Award className="w-4 h-4" />
              </div>
              <div>
                <span className="text-sm font-bold text-gray-900 dark:text-white font-outfit">Dual Stream Curriculum Completion</span>
                <p className="text-[11px] text-gray-400 dark:text-slate-400 font-plex">Tracks lessons completed across all enrolled live &amp; recorded courses</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`font-jetbrains text-base font-bold ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>{overallProgress}%</span>
            </div>
          </div>
          <div className="w-full h-3 bg-gray-100 dark:bg-white/[0.04] rounded-full overflow-hidden p-0.5">
            <div 
              className="h-full bg-gradient-to-r from-[#00FF88] via-[#00F0FF] to-emerald-400 rounded-full transition-all duration-1000 shadow-[0_0_12px_rgba(0,255,136,0.35)]" 
              style={{ width: `${overallProgress}%` }} 
            />
          </div>
          <div className="flex justify-between mt-2.5 px-1">
            <span className={`text-[10px] font-jetbrains font-semibold ${isLight ? "text-slate-500" : "text-slate-400"}`}>LEVEL 1 • FOUNDATIONS</span>
            <span className={`text-[10px] font-jetbrains font-semibold ${isLight ? "text-slate-500" : "text-slate-400"}`}>LEVEL 2 • INSTITUTIONAL ZONES</span>
            <span className={`text-[10px] font-jetbrains font-semibold ${isLight ? "text-slate-500" : "text-slate-400"}`}>LEVEL 3 • PRO TRADER</span>
          </div>
        </div>

        {/* ═══ Main Two-Column Layout ═══ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* ─── DUAL COURSES MASTER ARCHITECTURE ─── */}
            <div className={`rounded-2xl border transition-all ${
              isLight ? "bg-white border-slate-200/80 shadow-sm" : "bg-[#0F172A]/50 border-white/[0.06] shadow-xl"
            }`} id="recorded-courses">
              
              {/* Segmented Controller Header */}
              <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-white/[0.06] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-[#00F0FF]" />
                    <h2 className="text-base font-bold text-gray-900 dark:text-white font-outfit">
                      Course Ecosystem
                    </h2>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-slate-400 font-plex mt-0.5">
                    Unified 2-tier learning: Live Interactive Batches &amp; Recorded Masterclasses
                  </p>
                </div>

                {/* Apple-style Filter Pill Tabs */}
                <div className="flex items-center p-1 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] text-xs font-semibold font-outfit self-start sm:self-auto">
                  <button
                    onClick={() => setCourseFilter("all")}
                    className={`px-3 py-1.5 rounded-lg transition-all ${
                      courseFilter === "all"
                        ? "bg-white dark:bg-white/10 text-gray-900 dark:text-white shadow-xs"
                        : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    All Courses
                  </button>
                  <button
                    onClick={() => setCourseFilter("live")}
                    className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                      courseFilter === "live"
                        ? "bg-white dark:bg-white/10 text-rose-500 dark:text-rose-400 shadow-xs"
                        : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                    Live Courses
                  </button>
                  <button
                    onClick={() => setCourseFilter("recorded")}
                    className={`px-3 py-1.5 rounded-lg transition-all flex items-center gap-1.5 ${
                      courseFilter === "recorded"
                        ? "bg-white dark:bg-white/10 text-emerald-600 dark:text-[#00FF88] shadow-xs"
                        : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <Play className="w-3 h-3 fill-current" />
                    Recorded Courses
                  </button>
                </div>
              </div>

              {/* Course Content Body */}
              <div className="p-4 sm:p-6 space-y-6">

                {/* 1. Next Live Interactive Session Highlight (If Any) */}
                {upcomingLiveClass && (courseFilter === "all" || courseFilter === "live") && (
                  <div className="rounded-xl p-4 sm:p-5 border border-rose-500/30 bg-gradient-to-r from-rose-500/10 via-rose-500/5 to-transparent relative overflow-hidden">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-jetbrains font-bold bg-rose-500 text-white flex items-center gap-1">
                            <Radio className="w-3 h-3 animate-pulse" />
                            {upcomingLiveClass.status === "live" ? "HAPPENING NOW" : "UPCOMING LIVE BATCH"}
                          </span>
                          <span className="text-xs text-rose-500 dark:text-rose-300 font-jetbrains font-medium">
                            {upcomingLiveClass.course_title || "All Enrolled Students"}
                          </span>
                        </div>
                        <h4 className="text-base font-bold text-gray-900 dark:text-white font-outfit">
                          {upcomingLiveClass.title}
                        </h4>
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-slate-300 font-jetbrains">
                          {upcomingLiveClass.scheduled_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(upcomingLiveClass.scheduled_at).toLocaleDateString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {upcomingLiveClass.duration || 60} mins
                          </span>
                        </div>
                      </div>

                      <Link to="/live-classes" className="shrink-0">
                        {upcomingLiveClass.status === "live" ? (
                          <Button className="bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold text-xs rounded-xl shadow-lg shadow-emerald-500/30 animate-pulse">
                            <Radio className="w-4 h-4 mr-1.5" />
                            Join Live Now
                          </Button>
                        ) : (
                          <Button variant="outline" className="border-rose-400/40 text-rose-500 dark:text-rose-300 font-bold text-xs rounded-xl hover:bg-rose-500/10">
                            <Lock className="w-3.5 h-3.5 mr-1.5 text-amber-500" />
                            Opens at {upcomingLiveClass.scheduled_at ? new Date(upcomingLiveClass.scheduled_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "Scheduled Time"}
                          </Button>
                        )}
                      </Link>
                    </div>
                  </div>
                )}

                {/* 1. Enrolled Video Courses Section (Top Priority - Immediately visible without scroll) */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-emerald-500" />
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit uppercase tracking-wider">
                        My Enrolled Courses ({courses.length})
                      </h3>
                    </div>
                    <span className="text-[11px] text-emerald-600 dark:text-[#00FF88] font-jetbrains font-semibold bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                      Dual Stream Active
                    </span>
                  </div>

                  {courses.length === 0 ? (
                    <div className="text-center py-10 px-4 rounded-xl border border-dashed border-gray-200 dark:border-white/10">
                      <GraduationCap className="w-10 h-10 text-gray-400 dark:text-slate-500 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-gray-800 dark:text-white font-outfit">No Active Course Enrollment Found</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400 font-plex mt-1 max-w-md mx-auto">
                        Explore our professional curriculum below. One enrollment grants full dual access to live interactive batches and recorded video lessons.
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3.5">
                      {courses.map((course) => {
                        const courseProgress = Math.round(progress[course.course_id]?.progress_percentage || 0);
                        return (
                          <div 
                            key={course.course_id}
                            className={`rounded-xl border p-4 transition-all duration-200 group ${
                              isLight 
                                ? "bg-slate-50/70 border-slate-200 hover:border-emerald-500/50 hover:bg-white" 
                                : "bg-white/[0.02] border-white/[0.06] hover:border-emerald-500/30 hover:bg-white/[0.04]"
                            }`}
                            data-testid={`course-card-${course.course_id}`}
                          >
                            <div className="flex flex-col sm:flex-row gap-4">
                              <img 
                                src={course.thumbnail || "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=300"} 
                                alt={course.title} 
                                className="w-full sm:w-36 h-28 sm:h-24 rounded-lg object-cover border border-gray-200 dark:border-white/10 shrink-0" 
                              />
                              
                              <div className="flex-1 min-w-0 flex flex-col justify-between">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-[#00FF88] border-emerald-500/30 text-[10px] font-jetbrains font-semibold">
                                      ENROLLED
                                    </Badge>
                                    <span className="text-[11px] font-jetbrains text-slate-500 dark:text-slate-400">
                                      {course.sessions || 10}+ Sessions • HD Recordings + Notes
                                    </span>
                                  </div>

                                  <h4 className="text-base font-bold text-gray-900 dark:text-white font-outfit group-hover:text-emerald-500 transition-colors truncate">
                                    {course.title}
                                  </h4>
                                  <p className="text-xs text-gray-500 dark:text-slate-400 font-plex line-clamp-1 mt-0.5">
                                    {course.description}
                                  </p>
                                </div>

                                {/* Progress & Dual Stream Buttons */}
                                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.04] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="w-full sm:w-48">
                                    <div className="flex justify-between text-[11px] mb-1">
                                      <span className="text-gray-500 dark:text-slate-400 font-plex">Curriculum Progress</span>
                                      <span className="font-jetbrains font-bold text-emerald-500">{courseProgress}%</span>
                                    </div>
                                    <Progress value={courseProgress} className="h-1.5 bg-gray-200 dark:bg-white/10" />
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    <Link to="/live-classes">
                                      <Button size="sm" variant="outline" className="text-xs font-semibold h-8 rounded-lg border-rose-500/30 text-rose-500 hover:bg-rose-500/10">
                                        <Video className="w-3.5 h-3.5 mr-1" />
                                        Live Batch
                                      </Button>
                                    </Link>
                                    <Link to={`/course/${course.course_id}`}>
                                      <Button size="sm" className="bg-[#00FF88] hover:bg-[#00FF88]/90 text-black font-bold text-xs h-8 rounded-lg">
                                        <Play className="w-3.5 h-3.5 mr-1 fill-current" />
                                        Watch Recorded
                                      </Button>
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* 2. Live Batches (Rendered below courses, premium luxury design) */}
                {dashboardBatches.length > 0 && (courseFilter === "all" || courseFilter === "live") && (
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center text-white shadow-xs">
                          <Layers className="w-3.5 h-3.5" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit uppercase tracking-wider flex items-center gap-2">
                          <span>Live Batches</span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-400 font-bold border border-violet-500/20">
                            {dashboardBatches.length} Active
                          </span>
                        </h3>
                      </div>
                      <Link to="/batches" className="text-[11px] font-bold text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 transition-colors flex items-center gap-1 font-jetbrains">
                        Explore All Batches <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>

                    <div className="space-y-3">
                      {dashboardBatches.map((batch) => {
                        const isApproved = batch.my_enrollment_status === "approved" || user?.role === "admin" || user?.role === "superadmin" || user?.role === "mentor";
                        const isPending = batch.my_enrollment_status === "pending";
                        const isRejected = batch.my_enrollment_status === "rejected";

                        return (
                          <div
                            key={batch.batch_id}
                            className={`group relative rounded-2xl overflow-hidden border transition-all duration-300 shadow-xs hover:shadow-md ${
                              isLight
                                ? "bg-white border-slate-200/90 hover:border-violet-400"
                                : "bg-gradient-to-r from-[#121624] via-[#101420] to-[#0D101A] border-white/[0.08] hover:border-violet-500/40"
                            }`}
                            data-testid={`dashboard-batch-${batch.batch_id}`}
                          >
                            <div className="h-1 w-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500" />
                            
                            <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              {/* Left: Icon & Batch Details */}
                              <div className="flex items-center gap-3.5 sm:gap-4 flex-1 min-w-0">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-600 flex items-center justify-center text-white shadow-md shadow-violet-500/20 shrink-0">
                                  <Layers className="w-6 h-6" />
                                </div>
                                
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="text-[10px] font-jetbrains font-extrabold uppercase px-2 py-0.5 rounded-md bg-violet-500/10 text-violet-700 dark:text-violet-300 border border-violet-500/20">
                                      Live Batch
                                    </span>
                                    {batch.is_running_batch && (
                                      <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-700 dark:text-[#00FF88] border border-emerald-500/30 flex items-center gap-1.5 shadow-xs">
                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                        Active Batch
                                      </span>
                                    )}
                                    {isApproved && (
                                      <span className="text-[10px] font-extrabold uppercase font-jetbrains text-emerald-600 dark:text-[#00FF88] flex items-center gap-1">
                                        <CheckCircle className="w-3 h-3" /> Enrolled
                                      </span>
                                    )}
                                  </div>

                                  <h4 className={`text-base font-extrabold font-outfit truncate ${isLight ? "text-slate-900 group-hover:text-violet-700" : "text-white group-hover:text-violet-300"} transition-colors`}>
                                    {batch.name}
                                  </h4>

                                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-1 text-[11px] font-medium text-slate-500 dark:text-zinc-400">
                                    <span className="flex items-center gap-1.5">
                                      <Users className="w-3.5 h-3.5 text-violet-500" />
                                      <span>{batch.enrolled_count || 0} Students Enrolled</span>
                                    </span>
                                    {batch.start_date && (
                                      <span className="flex items-center gap-1.5">
                                        <Calendar className="w-3.5 h-3.5 text-fuchsia-500" />
                                        <span>{fmtDate(batch.start_date)}{batch.end_date && ` → ${fmtDate(batch.end_date)}`}</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Right: Only One Action Button - Enter Batch */}
                              <div className="shrink-0 flex items-center gap-2.5">
                                {isApproved ? (
                                  <Link to={`/batches?batch=${batch.batch_id}`}>
                                    <Button className="h-10 px-5 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                                      <span>Enter Batch</span>
                                      <ChevronRight className="w-4 h-4" />
                                    </Button>
                                  </Link>
                                ) : isPending ? (
                                  <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-amber-300/80 bg-amber-50/90 dark:bg-amber-500/10 dark:border-amber-500/30">
                                    <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-spin" />
                                    <span className="text-xs text-amber-800 dark:text-amber-300 font-bold whitespace-nowrap">
                                      Enrollment Under Review
                                    </span>
                                  </div>
                                ) : isRejected ? (
                                  <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-red-300/80 bg-red-50/90 dark:bg-red-500/10 dark:border-red-500/30">
                                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                                    <span className="text-xs text-red-800 dark:text-red-300 font-bold whitespace-nowrap">
                                      Not Approved
                                    </span>
                                  </div>
                                ) : (
                                  <Button
                                    disabled={!batch.is_active || enrollingBatchId === batch.batch_id}
                                    onClick={() => handleEnrollBatch(batch)}
                                    className="h-10 px-6 bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md whitespace-nowrap"
                                    data-testid={`enroll-batch-btn-${batch.batch_id}`}
                                  >
                                    {enrollingBatchId === batch.batch_id ? (
                                      <>
                                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                                        Requesting...
                                      </>
                                    ) : (
                                      <>
                                        <Plus className="w-3.5 h-3.5 mr-1" />
                                        Enroll into Batch
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 3. Available Courses (Catalog) with Unified Dual-Enrollment Guarantee */}
                {availableCourses.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-amber-500" />
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit uppercase tracking-wider">
                          Explore More Courses (Dual Stream Access)
                        </h3>
                      </div>
                      <span className="text-[11px] text-gray-400 font-plex hidden sm:inline">
                        1 Enrollment = Both Live &amp; Recorded Access
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3.5">
                      {availableCourses.map((course) => (
                        <div 
                          key={course.course_id}
                          className={`rounded-xl border p-4 transition-all duration-200 ${
                            isLight 
                              ? "bg-white border-slate-200 hover:border-slate-300 shadow-xs" 
                              : "bg-white/[0.02] border-white/[0.06] hover:border-white/15"
                          }`}
                          data-testid={`explore-course-${course.course_id}`}
                        >
                          <div className="flex flex-col sm:flex-row gap-4">
                            <img 
                              src={course.thumbnail || "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=300"} 
                              alt={course.title} 
                              className="w-full sm:w-36 h-28 sm:h-24 rounded-lg object-cover border border-gray-200 dark:border-white/10 shrink-0" 
                            />
                            
                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                              <div>
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className="px-2 py-0.5 rounded text-[10px] font-jetbrains font-bold bg-[#00FF88]/10 text-emerald-600 dark:text-[#00FF88] border border-[#00FF88]/20">
                                    LIVE + RECORDED INCLUDED
                                  </span>
                                  <span className="text-[11px] font-jetbrains text-gray-400">
                                    {course.sessions || 10} Sessions
                                  </span>
                                </div>

                                <h4 className="text-base font-bold text-gray-900 dark:text-white font-outfit">
                                  {course.title}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-slate-400 font-plex line-clamp-1 mt-0.5">
                                  {course.description}
                                </p>
                              </div>

                              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.04] flex items-center justify-between">
                                <div className="text-sm font-extrabold font-jetbrains text-emerald-600 dark:text-[#00FF88]">
                                  {course.price_display || (course.price === 0 ? "FREE" : `₹${course.price}`)}
                                </div>

                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedCourse(course);
                                    setShowEnrollModal(true);
                                  }}
                                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs h-8 px-4 rounded-lg shadow-sm flex items-center gap-1.5"
                                >
                                  <span>Enroll Dual Stream</span>
                                  <ChevronRight className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>

          {/* ─── RIGHT ACTION COLUMN (Streamlined & Apple-Grade) ─── */}
          <div className="space-y-5">
            
            {/* Drishti AI Quick Access Card */}
            <Link to="/drishti" className="block">
              <div className={`rounded-2xl p-5 border transition-all duration-300 group ${
                hasDrishtiAccess
                  ? isLight
                    ? "bg-white border-emerald-500/40 shadow-sm hover:border-emerald-500"
                    : "bg-[#0F172A]/60 border-emerald-500/30 hover:border-emerald-500/60 shadow-lg"
                  : isLight
                    ? "bg-white border-amber-400/40 shadow-sm hover:border-amber-400"
                    : "bg-[#0F172A]/60 border-amber-500/20 hover:border-amber-500/50 shadow-lg"
              }`} data-testid="drishti-quick">
                <div className="flex items-center gap-3.5 mb-2.5">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    hasDrishtiAccess
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-amber-500/10 text-amber-500"
                  }`}>
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit">Drishti AI</h3>
                      {hasDrishtiAccess ? (
                        <span className="text-[10px] font-jetbrains font-bold text-emerald-600 dark:text-[#00FF88] px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                          ACTIVE
                        </span>
                      ) : (
                        <span className="text-[9px] font-jetbrains font-bold text-amber-500 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> PRO LOCKED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 font-plex mt-0.5">
                      {hasDrishtiAccess
                        ? "Institutional demand/supply zones & ready setups"
                        : "Pro signals & setups • Admin approved access only"}
                    </p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-xs font-bold font-outfit mt-3 ${
                  hasDrishtiAccess ? "text-emerald-500" : "text-amber-500"
                } group-hover:gap-2.5 transition-all`}>
                  <span>{hasDrishtiAccess ? "Open Drishti Terminal" : "Unlock Drishti Pro Access"}</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>

            {/* Risk & Lot Calculator */}
            <Link to="/calculator" className="block">
              <div className={`rounded-2xl p-5 border transition-all duration-300 group ${
                hasDrishtiAccess
                  ? isLight 
                    ? "bg-white border-slate-200/80 hover:border-sky-400 shadow-sm" 
                    : "bg-[#0F172A]/60 border-white/[0.06] hover:border-sky-500/40 shadow-lg"
                  : isLight
                    ? "bg-white border-amber-400/40 shadow-sm hover:border-amber-400"
                    : "bg-[#0F172A]/60 border-amber-500/20 hover:border-amber-500/50 shadow-lg"
              }`} data-testid="calculator-quick">
                <div className="flex items-center gap-3.5 mb-2.5">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    hasDrishtiAccess ? "bg-sky-500/10 text-sky-500" : "bg-amber-500/10 text-amber-500"
                  }`}>
                    <Calculator className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit">Risk &amp; Lot Calculator</h3>
                      {hasDrishtiAccess ? (
                        <span className="text-[9px] font-jetbrains font-bold text-sky-500 uppercase px-2 py-0.5 rounded-full bg-sky-500/10">
                          Smart EV
                        </span>
                      ) : (
                        <span className="text-[9px] font-jetbrains font-bold text-amber-500 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> LOCKED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-slate-400 font-plex mt-0.5">
                      Safe 1-2% sizing, lot count &amp; risk reward calculator
                    </p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-xs font-bold font-outfit mt-3 ${
                  hasDrishtiAccess ? "text-sky-500" : "text-amber-500"
                } group-hover:gap-2.5 transition-all`}>
                  <span>{hasDrishtiAccess ? "Calculate Trade Position" : "Unlock Position Calculator"}</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>

            {/* Market Briefs */}
            <Link to="/briefs" className="block">
              <div className={`rounded-2xl p-5 border transition-all duration-300 group ${
                hasDrishtiAccess
                  ? isLight 
                    ? "bg-white border-slate-200/80 hover:border-slate-300 shadow-sm" 
                    : "bg-[#0F172A]/60 border-white/[0.06] hover:border-white/20 shadow-lg"
                  : isLight
                    ? "bg-white border-amber-400/40 shadow-sm hover:border-amber-400"
                    : "bg-[#0F172A]/60 border-amber-500/20 hover:border-amber-500/50 shadow-lg"
              }`} data-testid="briefs-quick">
                <div className="flex items-center gap-3.5 mb-2">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                    hasDrishtiAccess ? "bg-purple-500/10 text-purple-400" : "bg-amber-500/10 text-amber-500"
                  }`}>
                    <Newspaper className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit">Market Briefs</h3>
                      {hasDrishtiAccess ? (
                        <span className="text-[9px] font-jetbrains font-bold text-purple-400 uppercase px-2 py-0.5 rounded-full bg-purple-500/10">
                          DAILY
                        </span>
                      ) : (
                        <span className="text-[9px] font-jetbrains font-bold text-amber-500 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> LOCKED
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 dark:text-slate-400 font-plex mt-0.5">Pre &amp; Post Market institutional analysis</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-xs font-semibold font-outfit mt-3 ${
                  hasDrishtiAccess ? "text-purple-400" : "text-amber-500"
                } group-hover:gap-2.5 transition-all`}>
                  <span>{hasDrishtiAccess ? "Read Today's Brief" : "Unlock Market Briefs"}</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>

            {/* Trade of the Week (if available) */}
            {tradeOfWeek && (
              <div className={`rounded-2xl p-5 border ${
                isLight ? "bg-white border-amber-400/30" : "bg-[#0F172A]/60 border-amber-400/20 shadow-lg"
              }`} data-testid="trade-of-the-week">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  <p className="font-jetbrains text-[10px] tracking-[0.2em] text-amber-500 uppercase font-bold">TRADE OF THE WEEK</p>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-bold font-outfit text-gray-900 dark:text-white">{tradeOfWeek.stock_name}</p>
                    <p className="text-xs text-gray-400 font-plex">by {tradeOfWeek.trader_name}</p>
                  </div>
                  <Badge className={`${tradeOfWeek.trade_type === "long" ? "bg-emerald-500/15 text-emerald-500" : "bg-rose-500/15 text-rose-500"} border-0 text-[10px] font-jetbrains font-bold`}>
                    {tradeOfWeek.trade_type === "long" ? <ArrowUpRight className="w-3 h-3 mr-0.5" /> : <ArrowDownRight className="w-3 h-3 mr-0.5" />}
                    {tradeOfWeek.trade_type?.toUpperCase()}
                  </Badge>
                </div>
                <Link to="/journal" className="flex items-center gap-1 text-xs text-amber-500 font-bold hover:gap-2 transition-all">
                  {hasDrishtiAccess ? "Open Trade Journal" : "Unlock Trade Journal"} <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}

            {/* Referral Card */}
            <div className={`rounded-2xl p-5 border ${
              isLight ? "bg-white border-slate-200/80 shadow-sm" : "bg-[#0F172A]/60 border-white/[0.06] shadow-lg"
            }`} data-testid="referral-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-500" />
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white font-outfit">Your Referral Code</h3>
                </div>
                <span className="text-[10px] font-jetbrains text-emerald-500 font-semibold bg-emerald-500/10 px-2 py-0.5 rounded-full">
                  Earn Rewards
                </span>
              </div>
              <div className="bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <code className="text-base font-jetbrains font-extrabold text-emerald-500">{user?.referral_code || "N/A"}</code>
                  <Button variant="ghost" size="sm" onClick={copyReferralCode} className="text-gray-400 hover:text-white" data-testid="copy-referral-btn">
                    {copied ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-1.5 font-plex">Share your code with friends to unlock ecosystem perks</p>
              </div>
              <Link to="/leaderboard" className="block mt-3">
                <Button variant="outline" size="sm" className="w-full text-xs font-semibold rounded-xl border-gray-200 dark:border-white/10" data-testid="view-referrals-btn">
                  View Community Leaderboard <ChevronRight className="ml-1 w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>

            {/* Quick Links (Refined - No Messages or Recordings clutter) */}
            <div className={`rounded-2xl border overflow-hidden ${
              isLight ? "bg-white border-slate-200/80 shadow-sm" : "bg-[#0F172A]/60 border-white/[0.06] shadow-lg"
            }`} data-testid="quick-links">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-white/[0.06]">
                <h3 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider font-outfit">
                  Quick Navigation
                </h3>
              </div>
              <div className="p-2 space-y-0.5">
                {[
                  { to: "/live-classes", icon: Video, label: "Live Interactive Classes" },
                  { to: "/doubts", icon: Target, label: "Ask in Doubt Centre" },
                  { to: "/journal", icon: BookMarked, label: "My Trade Journal" },
                  { to: "/support", icon: Bell, label: "Contact Student Support" },
                  { to: "/payments", icon: TrendingUp, label: "View Fee & Payments" }
                ].map(link => (
                  <Link key={link.to} to={link.to} data-testid={`quick-link-${link.to.slice(1)}`}>
                    <Button variant="ghost" className="w-full justify-start text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.04] text-xs font-medium rounded-xl h-9">
                      <link.icon className="mr-2.5 w-4 h-4 text-emerald-500" /> {link.label}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ═══ UNIFIED DUAL-ENROLLMENT MODAL ═══ */}
      {showEnrollModal && selectedCourse && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-[#0B0F19] border border-gray-200 dark:border-white/[0.1] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-white/[0.06] bg-gradient-to-r from-emerald-500/10 via-transparent to-transparent">
              <div>
                <span className="text-[10px] font-jetbrains font-bold text-emerald-600 dark:text-[#00FF88] uppercase tracking-wider">
                  Unified Dual Stream Enrollment
                </span>
                <h3 className="text-lg font-extrabold text-gray-900 dark:text-white font-outfit mt-0.5">
                  {selectedCourse.title}
                </h3>
              </div>
              <button 
                onClick={() => { setShowEnrollModal(false); setSelectedCourse(null); }} 
                className="text-gray-400 hover:text-white rounded-lg p-1.5 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              
              {/* Dual Benefit Callout */}
              <div className="rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/20 space-y-2">
                <p className="text-xs font-bold text-emerald-600 dark:text-[#00FF88] font-outfit flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4" />
                  Your Single Enrollment Automatically Unlocks Both:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-plex text-gray-700 dark:text-slate-200 pt-1">
                  <div className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span><strong>Live Interactive Batches:</strong> Join live Zoom/Meet mentor sessions and Q&amp;A.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span><strong>Recorded Library:</strong> 24/7 self-paced modules, lecture replays &amp; PDF notes.</span>
                  </div>
                </div>
              </div>

              {/* Instant Automated Online Payment (Cashfree Gateway) */}
              {Number(selectedCourse.price || 0) > 0 && (
                <div className="rounded-xl p-4 bg-gradient-to-r from-emerald-500/15 via-cyan-500/10 to-indigo-500/10 border border-emerald-500/40 space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-1.5 text-[10px] font-jetbrains font-bold uppercase tracking-wider text-emerald-600 dark:text-[#00FF88]">
                        <Zap className="w-3.5 h-3.5 fill-current" />
                        <span>Instant Automated Activation</span>
                      </div>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-2xl sm:text-3xl font-black font-jetbrains text-gray-900 dark:text-white">
                          ₹{Number(selectedCourse.price).toLocaleString("en-IN")}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-slate-400 font-plex">One-time Dual Stream Fee</span>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-0.5">
                        Pay via UPI, Cards, NetBanking or Wallets. Access unlocks immediately.
                      </p>
                    </div>

                    <Button
                      onClick={handleOnlineCoursePayment}
                      disabled={payingOnline}
                      className="bg-gradient-to-r from-[#00FF88] to-[#00E5FF] hover:brightness-110 !text-black font-extrabold text-xs h-11 px-6 rounded-xl shadow-md shrink-0 flex items-center gap-2"
                      data-testid="pay-course-online-btn"
                    >
                      <Zap className="w-4 h-4 fill-black" />
                      <span>{payingOnline ? "Opening Checkout..." : "Pay Online Now"}</span>
                    </Button>
                  </div>

                  {/* Referral / Partner Code Input */}
                  <div className="pt-2 border-t border-emerald-500/20">
                    {!showDashboardRefInput && !dashboardRefCode ? (
                      <button
                        type="button"
                        onClick={() => setShowDashboardRefInput(true)}
                        className="text-[11px] font-semibold text-emerald-600 dark:text-[#00FF88] hover:underline flex items-center gap-1.5 transition-colors"
                      >
                        <Tag className="w-3 h-3" />
                        <span>Have a Referral or Partner Code?</span>
                      </button>
                    ) : (
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-semibold flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                            <Gift className="w-3 h-3 text-emerald-500" />
                            <span>Referral Code (Optional)</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              setDashboardRefCode("");
                              setShowDashboardRefInput(false);
                              try {
                                sessionStorage.removeItem("jamadhan_referral_code");
                                localStorage.removeItem("jamadhan_referral_code");
                              } catch (_) {}
                            }}
                            className="text-[10px] text-gray-400 hover:text-red-400 flex items-center gap-0.5"
                          >
                            <X className="w-2.5 h-2.5" /> Remove
                          </button>
                        </div>
                        <input
                          type="text"
                          value={dashboardRefCode}
                          onChange={(e) => {
                            const val = e.target.value.toUpperCase();
                            setDashboardRefCode(val);
                            try {
                              if (val.trim()) {
                                sessionStorage.setItem("jamadhan_referral_code", val.trim());
                              } else {
                                sessionStorage.removeItem("jamadhan_referral_code");
                                localStorage.removeItem("jamadhan_referral_code");
                              }
                            } catch (_) {}
                          }}
                          placeholder="e.g. JAM-ABCD12"
                          maxLength={20}
                          className="w-full px-2.5 py-1.5 text-xs font-mono font-bold tracking-wider rounded-lg border uppercase outline-none bg-white dark:bg-black/40 border-gray-200 dark:border-white/10 text-emerald-600 dark:text-[#00FF88] focus:border-emerald-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider mb-2 font-outfit">
                  Or Request Access from Admin (Trial / Bank Transfer):
                </p>
                
                <div className="space-y-2.5">
                  {[
                    { id: "trial", label: "TRIAL EVALUATION", desc: "Request temporary access to evaluate both live and recorded curriculum." },
                    { id: "paid", label: "PAID LIFETIME ACCESS", desc: "Full lifetime access to live batches, archives & notes after payment." },
                    { id: "old_student", label: "EXISTING STUDENT EXTENSION", desc: "Enrolled JamaDhan student requesting batch extension or reactivation." }
                  ].map((option) => (
                    <label 
                      key={option.id} 
                      className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                        enrollType === option.id 
                          ? "bg-emerald-500/10 border-emerald-500/50 shadow-xs" 
                          : "bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] hover:border-gray-300 dark:hover:border-white/20"
                      }`}
                    >
                      <input 
                        type="radio" 
                        name="enrollType" 
                        value={option.id} 
                        checked={enrollType === option.id} 
                        onChange={() => setEnrollType(option.id)}
                        className="sr-only"
                      />
                      <div className={`w-4 h-4 rounded-full border mt-0.5 flex items-center justify-center shrink-0 ${
                        enrollType === option.id ? "border-emerald-500" : "border-gray-400 dark:border-slate-500"
                      }`}>
                        {enrollType === option.id && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                      </div>
                      <div>
                        <p className={`font-bold text-xs font-outfit ${enrollType === option.id ? "text-emerald-600 dark:text-white" : "text-gray-800 dark:text-slate-300"}`}>
                          {option.label}
                        </p>
                        <p className="text-[11px] text-gray-500 dark:text-slate-400 font-plex mt-0.5">{option.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              <p className="text-[11px] text-gray-400 dark:text-slate-400 font-plex text-center">
                Submitting this sends a priority request to the academy admin. Once verified, access to both Live Sessions &amp; Recorded Modules is activated simultaneously.
              </p>
            </div>

            <div className="p-5 border-t border-gray-100 dark:border-white/[0.06] flex items-center justify-end gap-3 bg-gray-50/50 dark:bg-white/[0.01]">
              <Button 
                variant="ghost" 
                onClick={() => { setShowEnrollModal(false); setSelectedCourse(null); }} 
                className="text-xs font-semibold text-gray-500 dark:text-slate-400 hover:text-white rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                onClick={submitEnrollmentRequest} 
                disabled={enrolling} 
                className="bg-gradient-to-r from-[#00FF88] to-[#00E5FF] text-black font-bold text-xs px-5 py-2.5 rounded-xl shadow-[0_4px_16px_rgba(0,255,136,0.25)] hover:brightness-110"
              >
                {enrolling ? "Submitting Request..." : "Confirm & Send Request"}
              </Button>
            </div>

          </div>
        </div>
      )}

      {/* ═══ Apple/Linear-Grade Broadcast & Alert Center Modal ═══ */}
      <Dialog open={isBroadcastModalOpen} onOpenChange={setIsBroadcastModalOpen}>
        <DialogContent className={`max-w-4xl w-[95vw] h-[85vh] max-h-[760px] p-0 overflow-hidden border shadow-2xl rounded-3xl flex flex-col ${
          isLight ? "bg-white border-slate-200 text-slate-900" : "bg-[#0B0F19] border-white/10 text-white"
        }`}>
          {/* Top Bar */}
          <div className={`px-6 py-4 border-b flex items-center justify-between shrink-0 ${
            isLight ? "bg-slate-50/80 border-slate-200" : "bg-white/[0.02] border-white/10"
          }`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20">
                <Megaphone className="w-4 h-4" />
              </div>
              <div>
                <DialogTitle className="text-base sm:text-lg font-extrabold font-outfit text-gray-900 dark:text-white leading-tight">
                  Admin Broadcasts &amp; Alerts
                </DialogTitle>
                <p className={`text-xs font-plex ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                  Official announcements, class schedules &amp; trading alerts
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {unreadBroadcastsCount > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-jetbrains font-bold bg-rose-500/10 text-rose-500 border border-rose-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping" />
                  {unreadBroadcastsCount} Unread
                </span>
              )}

              <button
                onClick={() => setIsBroadcastModalOpen(false)}
                className={`p-2 rounded-xl transition-all ${
                  isLight ? "text-slate-400 hover:text-slate-800 hover:bg-slate-200" : "text-slate-400 hover:text-white hover:bg-white/10"
                }`}
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile Tab Switcher */}
          <div className="md:hidden flex border-b border-slate-200 dark:border-white/10 px-4 py-2 gap-2 bg-slate-50/50 dark:bg-white/[0.01] shrink-0">
            <button
              onClick={() => setBroadcastMobileTab("message")}
              className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold font-outfit transition-all ${
                broadcastMobileTab === "message"
                  ? isLight ? "bg-white text-indigo-600 shadow-xs border border-slate-200 font-bold" : "bg-indigo-600 text-white font-bold"
                  : isLight ? "text-slate-600" : "text-slate-400"
              }`}
            >
              Reading Message
            </button>
            <button
              onClick={() => setBroadcastMobileTab("list")}
              className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold font-outfit transition-all ${
                broadcastMobileTab === "list"
                  ? isLight ? "bg-white text-indigo-600 shadow-xs border border-slate-200 font-bold" : "bg-indigo-600 text-white font-bold"
                  : isLight ? "text-slate-600" : "text-slate-400"
              }`}
            >
              All Messages ({broadcasts.length})
            </button>
          </div>

          {/* Main Body Grid */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-12 min-h-0 overflow-hidden">
            {/* Left Column: Feed List */}
            <div className={`md:col-span-5 border-r border-slate-200 dark:border-white/10 flex flex-col min-h-0 ${
              isLight ? "bg-slate-50/40" : "bg-white/[0.01]"
            } ${broadcastMobileTab === "list" ? "block" : "hidden md:flex"}`}>
              <div className="px-4 py-3 border-b border-slate-200/80 dark:border-white/[0.06] flex items-center justify-between shrink-0">
                <span className={`text-xs font-jetbrains font-bold uppercase tracking-wider ${
                  isLight ? "text-slate-500" : "text-zinc-400"
                }`}>
                  Feed History ({broadcasts.length})
                </span>
                <span className={`text-[11px] font-jetbrains ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                  Click to read
                </span>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
                {broadcasts.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <Megaphone className="w-8 h-8 text-slate-300 dark:text-zinc-600 mx-auto mb-2" />
                    <p className={`text-xs font-plex ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                      No broadcast announcements or alerts currently posted.
                    </p>
                  </div>
                ) : (
                  broadcasts.map((b) => {
                    const isSelected = selectedBroadcast && (selectedBroadcast.notification_id || selectedBroadcast.id) === (b.notification_id || b.id);
                    const isUnread = !b.read_by?.includes(user?.user_id);
                    const meta = getBroadcastMeta(b.type, b.title);
                    const IconComponent = meta.icon;

                    return (
                      <button
                        key={b.notification_id || b.id}
                        onClick={() => {
                          setSelectedBroadcast(b);
                          setBroadcastMobileTab("message");
                        }}
                        className={`w-full text-left p-3.5 rounded-2xl border transition-all flex flex-col gap-1.5 relative group ${
                          isSelected
                            ? isLight
                              ? "bg-white border-indigo-400/90 shadow-md ring-1 ring-indigo-400/30"
                              : "bg-indigo-950/40 border-indigo-500/50 shadow-lg shadow-indigo-950/40 ring-1 ring-indigo-500/30"
                            : isLight
                              ? "bg-white hover:bg-slate-100/80 border-slate-200/80 hover:border-slate-300"
                              : "bg-white/[0.02] hover:bg-white/[0.05] border-white/5 hover:border-white/10"
                        }`}
                      >
                        {/* Top tag & time */}
                        <div className="flex items-center justify-between gap-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-jetbrains font-bold uppercase ${meta.badge}`}>
                            <IconComponent className="w-3 h-3" />
                            {meta.label}
                          </span>
                          
                          <span className={`text-[10px] font-jetbrains shrink-0 ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                            {b.created_at ? new Date(b.created_at).toLocaleDateString("en-IN", { month: "short", day: "numeric" }) : ""}
                          </span>
                        </div>

                        {/* Title */}
                        <div className="flex items-start gap-1.5 mt-0.5">
                          {isUnread && (
                            <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0 mt-1.5 animate-pulse" />
                          )}
                          <p className={`text-xs sm:text-sm font-bold font-outfit line-clamp-2 ${
                            isSelected
                              ? isLight ? "text-indigo-950" : "text-white"
                              : isLight ? "text-slate-800" : "text-slate-200"
                          }`}>
                            {b.title || "Admin Broadcast"}
                          </p>
                        </div>

                        {/* Snippet preview */}
                        <p className={`text-xs font-plex line-clamp-1 ${
                          isLight ? "text-slate-500" : "text-zinc-400"
                        }`}>
                          {b.message}
                        </p>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Column: Full Dedicated Message View */}
            <div className={`md:col-span-7 flex flex-col min-h-0 bg-white dark:bg-[#0B0F19] ${
              broadcastMobileTab === "message" ? "block" : "hidden md:flex"
            }`}>
              {selectedBroadcast ? (
                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8 flex flex-col justify-between">
                  <div className="space-y-6">
                    {/* Meta Bar */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-jetbrains font-bold uppercase tracking-wider ${
                          getBroadcastMeta(selectedBroadcast.type, selectedBroadcast.title).badge
                        }`}>
                          {getBroadcastMeta(selectedBroadcast.type, selectedBroadcast.title).label}
                        </span>

                        <span className={`text-xs font-jetbrains font-medium ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                          {selectedBroadcast.created_at ? new Date(selectedBroadcast.created_at).toLocaleString("en-IN", {
                            day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                          }) : ""}
                        </span>
                      </div>

                      {/* Prominent Headline */}
                      <h2 className="text-xl sm:text-2xl font-extrabold font-outfit text-gray-900 dark:text-white leading-tight">
                        {selectedBroadcast.title || "Admin Broadcast"}
                      </h2>

                      {/* Official Verified Badge */}
                      <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                        <ShieldCheck className="w-4 h-4 shrink-0 text-emerald-500" />
                        <span>Official Verified Broadcast from Jamadhan Admin Team</span>
                      </div>
                    </div>

                    {/* Rich Message Body Card */}
                    <div className={`rounded-2xl p-6 sm:p-7 border shadow-sm ${
                      isLight
                        ? "bg-slate-50/80 border-slate-200 text-slate-800"
                        : "bg-white/[0.03] border-white/[0.08] text-slate-100"
                    }`}>
                      <div className="text-sm sm:text-base font-plex leading-relaxed whitespace-pre-wrap select-text">
                        {selectedBroadcast.message}
                      </div>
                    </div>

                    {/* Contextual Action Link (if class reminder or alert) */}
                    {(selectedBroadcast.type === "class_reminder" || selectedBroadcast.title?.toLowerCase().includes("class") || selectedBroadcast.title?.toLowerCase().includes("batch")) && (
                      <div className="pt-2">
                        <Link to="/live-classes" onClick={() => setIsBroadcastModalOpen(false)}>
                          <Button className="bg-sky-600 hover:bg-sky-700 !text-white font-bold text-xs rounded-xl h-10 px-5 shadow-sm flex items-center gap-2">
                            <Video className="w-4 h-4" />
                            <span>Go to Live Classes &amp; Join Batch</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      </div>
                    )}

                    {(selectedBroadcast.type === "alert" || selectedBroadcast.type === "drishti_alert" || selectedBroadcast.title?.toLowerCase().includes("drishti")) && (
                      <div className="pt-2">
                        <Link to="/drishti" onClick={() => setIsBroadcastModalOpen(false)}>
                          <Button className="bg-gradient-to-r from-amber-500 to-amber-600 hover:brightness-110 !text-black font-bold text-xs rounded-xl h-10 px-5 shadow-sm flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            <span>Open Drishti AI Terminal</span>
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>

                  <div className="pt-6 mt-6 border-t border-slate-200 dark:border-white/10 flex items-center justify-between text-xs text-slate-400">
                    <span>Audience: Enrolled Academy Students</span>
                    <span>Jamadhan Academic Network</span>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <Megaphone className="w-12 h-12 text-slate-300 dark:text-zinc-600 mb-3" />
                  <p className="text-base font-bold font-outfit text-slate-700 dark:text-slate-300">
                    Select a broadcast message to read
                  </p>
                  <p className="text-xs font-plex text-slate-400 mt-1">
                    Choose any announcement from the left feed
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Modal Footer */}
          <div className={`px-6 py-4 border-t flex items-center justify-between shrink-0 ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.02] border-white/10"
          }`}>
            <Button
              onClick={handleMarkAsRead}
              disabled={markingRead || unreadBroadcastsCount === 0}
              variant="outline"
              size="sm"
              className={`text-xs font-jetbrains font-semibold rounded-xl h-9 ${
                isLight ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/10 text-white hover:bg-white/5"
              }`}
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
              {unreadBroadcastsCount > 0 ? `Mark All as Read (${unreadBroadcastsCount})` : "All Caught Up"}
            </Button>

            <Button
              onClick={() => setIsBroadcastModalOpen(false)}
              className="bg-indigo-600 hover:bg-indigo-700 !text-white font-bold text-xs rounded-xl h-9 px-6 shadow-sm"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default StudentDashboard;
